/** Structured terms for each claimable special deal. Shared by the claim form and the server (server re-prices). */
export type PaperRule = 'choose' | 'both' | 'Red Hook Star-Revue' | 'The Village Star-Revue';
export interface DealTerms {
  key: string;
  name: string;
  adSize: string;
  issues: number;            // consecutive monthly issues
  fixedStart?: string;       // e.g. "November 2026" — no choice
  papers: PaperRule;
  total: number;             // whole package price
  section?: 'Special Section';
  sectionName?: string;
  /** Customer picks an ad size; price = one-time rate from the Ad Sizes board × this factor. */
  sizeFactor?: number;
}

export const DEALS: DealTerms[] = [
  { key: 'twofer-quarter', name: 'End-of-year twofer: two Quarter Page ads', adSize: 'Quarter Page', issues: 2, fixedStart: 'November 2026', papers: 'choose', total: 300 },
  { key: 'twofer-half', name: 'End-of-year twofer: two Half Page ads', adSize: 'Half Page', issues: 2, fixedStart: 'November 2026', papers: 'choose', total: 500 },
  { key: 'twofer-full', name: 'End-of-year twofer: two Full Page ads', adSize: 'Full Page', issues: 2, fixedStart: 'November 2026', papers: 'choose', total: 850 },
  { key: 'new-business', name: 'New Business Special', adSize: 'Eighth Page', issues: 3, papers: 'choose', total: 150, section: 'Special Section', sectionName: 'New Businesses' },
];

/** Board-written deals (Special Deals board) matched by name to structured terms. */
export function termsForBoardDeal(name: string): DealTerms | null {
  const n = name.toLowerCase();
  if (n.includes('holiday quarter')) return { key: 'board-holiday-quarter', name, adSize: 'Quarter Page', issues: 4, papers: 'Red Hook Star-Revue', total: 600 };
  if (n.includes('double-star') || n.includes('combo')) return { key: 'board-combo', name, adSize: 'Quarter Page', issues: 1, papers: 'both', total: 250 };
  if (n.includes('new advertiser')) return { key: 'board-new-advertiser', name, adSize: '', issues: 1, papers: 'The Village Star-Revue', total: 0, sizeFactor: 0.5 };
  if (n.includes('maximum coverage')) return { key: 'board-max-coverage', name, adSize: 'Full Page', issues: 1, papers: 'choose', total: 600 };
  return null;
}

export const dealByKey = (key: string): DealTerms | null => DEALS.find((d) => d.key === key)
  ?? (key === 'board-holiday-quarter' ? termsForBoardDeal('holiday quarter') : key === 'board-combo' ? termsForBoardDeal('combo') : key === 'board-max-coverage' ? termsForBoardDeal('maximum coverage') : key === 'board-new-advertiser' ? termsForBoardDeal('new advertiser') : null);

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function addMonths(label: string, n: number): string {
  const [m, y] = label.trim().split(/\s+/);
  const idx = MONTHS.indexOf(m ?? '');
  const year = Number(y);
  if (idx < 0 || !year) return label;
  const t = idx + n;
  return `${MONTHS[((t % 12) + 12) % 12]} ${year + Math.floor(t / 12)}`;
}
/** Next `count` issue months starting next month. */
export function upcomingMonths(count = 6): string[] {
  const d = new Date();
  return Array.from({ length: count }, (_, i) => { const x = new Date(d.getFullYear(), d.getMonth() + 1 + i, 1); return `${MONTHS[x.getMonth()]} ${x.getFullYear()}`; });
}
export const PAY_CHOICES = ['Credit Card', 'Zelle', 'Venmo', 'PayPal', 'Bill me later'] as const;
export type PayChoice = (typeof PAY_CHOICES)[number];
/** "Both papers" on a choose-your-paper deal costs double. */
export const priceFor = (base: number, deal: DealTerms, bothPapers: boolean) => (deal.papers === 'choose' && bothPapers ? base * 2 : base);
